import { Box, Collapse, IconButton, Skeleton, SxProps, Theme, Tooltip } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React from "react";
import { collapseMinHeight, useDotLegalCollapseStyles } from "./DotLegalCollapse.styles";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { Guid } from "../../guid";
import { DotLegalButton, ToDto, UrlToPathObject } from "@dotlegal/dotlegal-ui-components";
import { HashLink } from "react-router-hash-link";

export interface IDotLegalCollapse {
    collapseName?: string | JSX.Element;
    children?: any;
    isExpanded: boolean;
    centerContent?: any;
    onExpandClick: () => void;
    removeItemText?: string;
    removeItemSize?: "small" | "medium" | "large";
    removeItemStyle?: "button" | "link";
    onRemoveClick?: () => void;
    className?: string;
    sx?: SxProps<Theme> | undefined;
    listKey?: string | number;
    disabled?: boolean;
    objectId?: string;
    objectName?: string;
    isLoading?: boolean;
    error?: boolean;
    collapseIcon?: { icon: JSX.Element; iconHoverText: string; href?: ToDto | string; replaceIcon?: boolean };
    id?: string;
    persistCollapseName?: boolean;
    hideCollapseCenterName?: boolean;
    isCollapseNameBold?: boolean;
    leftAlign?: boolean;
    collapseStyle?: "primary" | "secondary";
    invertExpandedArrow?: boolean;
    color?: "blue" | "green";
    headerColor?: "primary" | "grey";
    arrowFontSize?: number;
}

function DotLegalCollapse(props: IDotLegalCollapse) {
    const styles = useDotLegalCollapseStyles(props);
    const getIconForCollapse = () => {
        if (props.collapseIcon) {
            return (
                <Box className="collapseSVG" sx={{ paddingRight: 2 }}>
                    <Tooltip title={props.collapseIcon.iconHoverText}>
                        {props.collapseIcon.href ? (
                            <Box sx={styles.collapseIcons}>
                                <HashLink smooth to={UrlToPathObject(props.collapseIcon.href)} onClick={(event) => event.stopPropagation()}>
                                    <IconButton sx={styles.collapseIcon}>{props.collapseIcon.icon}</IconButton>
                                </HashLink>
                            </Box>
                        ) : (
                            props.collapseIcon.icon
                        )}
                    </Tooltip>
                </Box>
            );
        }
    };

    const GetCollapseArrow = () => {
        return (
            <React.Fragment>
                {props.isExpanded ? (
                    <>
                        {getIconForCollapse()}

                        <Box sx={styles.collapseIcons}>
                            <IconButton sx={styles.expandArrowButton} onClick={props.onExpandClick}>
                                {props.invertExpandedArrow ? (
                                    <KeyboardArrowDownIcon fontSize="large" sx={{ fontSize: props.arrowFontSize }} />
                                ) : (
                                    <KeyboardArrowUpIcon fontSize="large" sx={{ fontSize: props.arrowFontSize }} />
                                )}
                            </IconButton>
                        </Box>
                    </>
                ) : (
                    <Box sx={styles.collapseIcons}>
                        {getIconForCollapse()}
                        {!props.collapseIcon?.replaceIcon && (
                            <IconButton sx={styles.expandArrowButton} onClick={props.onExpandClick}>
                                {props.invertExpandedArrow ? (
                                    <KeyboardArrowUpIcon fontSize="large" sx={{ fontSize: props.arrowFontSize }} />
                                ) : (
                                    <KeyboardArrowDownIcon fontSize="large" sx={{ fontSize: props.arrowFontSize }} />
                                )}
                            </IconButton>
                        )}
                    </Box>
                )}
            </React.Fragment>
        );
    };

    // The centre column is only worth reserving when something actually renders in it, otherwise a long
    // collapse name is squeezed into a quarter of the header and wraps while half the row sits empty.
    const hasCenterContent = !props.hideCollapseCenterName && (props.centerContent !== undefined || props.isLoading);

    const GetCollapsedJSX = () => {
        return (
            <Collapse
                className={props.className}
                sx={[styles.collapse, ...(Array.isArray(props.sx) ? props.sx : [props.sx])]}
                in={props.isExpanded}
                collapsedSize={collapseMinHeight}
            >
                {!props.isExpanded && (
                    <Grid container sx={styles.gridContainer}>
                        <Grid size={hasCenterContent ? 3 : 9}>{props.collapseName && <div>{props.collapseName}</div>}</Grid>
                        {hasCenterContent && (
                            <Grid size={6}>
                                {props.isLoading ? (
                                    <Box sx={styles.skeletonContainer}>
                                        <Skeleton width={"30%"} />
                                    </Box>
                                ) : (
                                    <Box sx={styles.gridContainerCenter}>{props.centerContent}</Box>
                                )}
                            </Grid>
                        )}
                        <Grid sx={styles.gridContainerRight} size={3}>
                            {GetCollapseArrow()}
                        </Grid>
                    </Grid>
                )}
                {props.isExpanded && (
                    <React.Fragment>
                        <Grid container sx={styles.gridContainer}>
                            <Grid size={9}>{props.collapseName && props.persistCollapseName && <div>{props.collapseName}</div>}</Grid>
                            <Grid sx={styles.gridContainerRight} size={3}>
                                {GetCollapseArrow()}
                            </Grid>
                        </Grid>
                        <Box sx={styles.collapseChild} className={`collapseChildContainer`}>
                            {props.children}
                            {props.removeItemText && !props.disabled && (
                                <Box sx={styles.removeBtnContainer}>
                                    <DotLegalButton
                                        buttonType={props.removeItemStyle === "link" ? "dangerLinkButton" : "danger"}
                                        size={props.removeItemSize}
                                        onClick={props.onRemoveClick}
                                    >
                                        {props.removeItemText}
                                    </DotLegalButton>
                                </Box>
                            )}
                        </Box>
                    </React.Fragment>
                )}
            </Collapse>
        );
    };

    return (
        <React.Fragment key={props.listKey ?? Guid.newGuid.toString()}>
            <Box id={props.id}>
                {!props.isExpanded ? (
                    <Box onClick={props.onExpandClick} sx={styles.onCollapsed}>
                        {GetCollapsedJSX()}{" "}
                    </Box>
                ) : (
                    GetCollapsedJSX()
                )}
            </Box>
        </React.Fragment>
    );
}

export default DotLegalCollapse;
